import { LocationDescriptor } from "history";
import { observer, useObserver } from "mobx-react";
import { CommentIcon, PlusIcon } from "outline-icons";
import * as React from "react";
import { useRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { TeamPreference } from "@shared/types";
import Document from "~/models/Document";
import Revision from "~/models/Revision";
import { openDocumentInsights } from "~/actions/definitions/documents";
import DocumentMeta, { Separator } from "~/components/DocumentMeta";
import Fade from "~/components/Fade";
import useCurrentTeam from "~/hooks/useCurrentTeam";
import { useLocationSidebarContext } from "~/hooks/useLocationSidebarContext";
import usePolicy from "~/hooks/usePolicy";
import useStores from "~/hooks/useStores";
import { DataAttributesHelper } from "~/utils/DataAttributesHelper";
import breakpoint from "styled-components-breakpoint";
import { documentPath } from "~/utils/routeHelpers";
import { Properties, PropertiesRef } from "./Properties";
import NudeButton from "~/components/NudeButton";
import { useMenuAction } from "~/hooks/useMenuAction";
import { createActionV2 } from "~/actions";
import { DropdownMenu } from "~/components/Menu/DropdownMenu";
import { useComputed } from "~/hooks/useComputed";

type Props = {
  /* The document to display meta data for */
  document: Document;
  revision?: Revision;
  to?: LocationDescriptor;
  rtl?: boolean;
};

function TitleDocumentMeta({ to, document, revision, ...rest }: Props) {
  const { views, comments, dataAttributes, ui } = useStores();
  const { t } = useTranslation();
  const sidebarContext = useLocationSidebarContext();
  const team = useCurrentTeam();
  const documentViews = useObserver(() => views.inDocument(document.id));
  const totalViewers = documentViews.length;
  const onlyYou = totalViewers === 1 && documentViews[0].userId;
  const viewsLoadedOnMount = useRef(totalViewers > 0);
  const can = usePolicy(document);
  const canTeam = usePolicy(team);
  const propertiesRef = React.useRef<PropertiesRef>(null);

  const Wrapper = viewsLoadedOnMount.current ? Fragment : Fade;

  const commentsCount = comments.unresolvedCommentsInDocumentCount(document.id);

  const dataAttributesAvailable =
    canTeam.listDataAttribute && dataAttributes.orderedData.length > 0;
  const missingDataAttributes =
    !document.dataAttributes ||
    document.dataAttributes?.length < dataAttributes.orderedData.length;
  const commentingEnabled = !!team.getPreference(TeamPreference.Commenting);

  return (
    <>
      <Meta document={document} revision={revision} to={to} replace {...rest}>
        {commentingEnabled && can.comment && (
          <>
            <Separator />
            <InlineLink
              to={{
                pathname: documentPath(document),
                state: { sidebarContext },
              }}
              onClick={() => ui.toggleComments()}
            >
              <CommentIcon size={18} />
              {commentsCount
                ? t("{{ count }} comment", { count: commentsCount })
                : t("Comment")}
            </InlineLink>
          </>
        )}
        {dataAttributesAvailable && missingDataAttributes && can.update ? (
          <>
            <Separator />
            <AddPropertyMenu
              document={document}
              propertiesRef={propertiesRef}
            />
          </>
        ) : null}
        {totalViewers &&
        can.listViews &&
        !document.isDraft &&
        !document.isTemplate ? (
          <Wrapper>
            <Separator />
            <InsightsButton action={openDocumentInsights}>
              {t("Viewed by")}{" "}
              {onlyYou
                ? t("only you")
                : `${totalViewers} ${
                    totalViewers === 1 ? t("person") : t("people")
                  }`}
            </InsightsButton>
          </Wrapper>
        ) : null}
      </Meta>
      <Properties document={document} ref={propertiesRef} />
    </>
  );
}

const AddPropertyMenu = ({
  propertiesRef,
  document,
}: {
  document: Document;
  propertiesRef: React.RefObject<PropertiesRef>;
}) => {
  const { dataAttributes } = useStores();
  const { t } = useTranslation();
  const section = "Data Attribute";

  const actions = useComputed(
    () =>
      dataAttributes.orderedData
        .filter(
          (a) =>
            !a.deletedAt &&
            !document.dataAttributes?.find((d) => d.dataAttributeId === a.id)
        )
        .map((attribute) =>
          createActionV2({
            name: attribute.name,
            icon: DataAttributesHelper.getIcon(
              attribute.dataType,
              attribute.name
            ),
            section,
            perform: () => propertiesRef.current?.addProperty(attribute.id),
          })
        ),
    [dataAttributes.orderedData, document, propertiesRef, t]
  );

  const rootAction = useMenuAction(actions);

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu action={rootAction} ariaLabel={t("Show menu")}>
      <InlineLink to={documentPath(document)}>
        <PlusIcon size={18} /> {t("Property")}
      </InlineLink>
    </DropdownMenu>
  );
};

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const InsightsButton = styled(NudeButton)`
  background: none;
  border: none;
  padding: 0;
  width: auto;
  height: auto;
  color: inherit;
  font: inherit;
  text-decoration: none;
  cursor: var(--pointer);

  &:hover {
    text-decoration: underline;
  }
`;

export const Meta = styled(DocumentMeta)<{ rtl?: boolean }>`
  justify-content: ${(props) => (props.rtl ? "flex-end" : "flex-start")};
  margin: -12px 0 2em 0;
  font-size: 14px;
  position: relative;
  user-select: none;
  z-index: 1;

  ${breakpoint("mobile", "tablet")`
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.6;

    ${Separator} {
      display: none;
    }
  `}

  a {
    color: inherit;
    cursor: var(--pointer);

    &:hover {
      text-decoration: underline;
    }
  }

  @media print {
    display: none;
  }
`;

export default observer(TitleDocumentMeta);
